import { getState, createPageAgg, createFormAgg, createSectionAgg, createFieldAgg } from '/bb/state.js';
import { bumpPageLifetime, bumpFormLifetime, lifetimeFor } from '/bb/storage.js';
import { currentPageKey, pageFormKey, pageFormFieldKey, pageSectionKey } from '/bb/identifier.js';
import { VISIT_DEBOUNCE_MS } from '/bb/constants.js';

/** Stable wall-clock ms for serialization. */
export function nowMs() {
  return Date.now();
}

/** Get or create the page aggregator and ensure it is the "current" page. */
export function ensureCurrentPage() {
  const s = getState();
  const key = currentPageKey();
  if (s.currentPageKey === key) {
    if (!s.pages.has(key)) s.pages.set(key, createPageAgg());
    if (s.currentPageEnteredAt <= 0) s.currentPageEnteredAt = nowMs();
    return key;
  }
  const t = nowMs();
  if (s.currentPageKey && s.currentPageEnteredAt > 0) {
    const prev = s.pages.get(s.currentPageKey);
    if (prev) prev.duration_ms += Math.max(0, t - s.currentPageEnteredAt);
  }
  let agg = s.pages.get(key);
  if (!agg) {
    agg = createPageAgg();
    s.pages.set(key, agg);
  }
  if (t - agg.lastVisitMarkAt >= VISIT_DEBOUNCE_MS) {
    agg.lastVisitMarkAt = t;
    agg.visit_count_lifetime = bumpPageLifetime(key, t);
  } else {
    const lt = lifetimeFor(key, '');
    agg.visit_count_lifetime = lt.page;
  }
  agg.visit_count = (agg.visit_count || 0) + 1;
  s.currentPageKey = key;
  s.currentPageEnteredAt = t;
  return key;
}

export function getOrCreateForm(pageKey, formId) {
  const s = getState();
  const key = pageFormKey(pageKey, formId);
  let f = s.forms.get(key);
  if (!f) {
    f = createFormAgg();
    f.pageKey = pageKey;
    f.formId = formId;
    const lt = lifetimeFor(pageKey, formId);
    f.visit_count_lifetime = lt.form;
    s.forms.set(key, f);
  }
  return f;
}

export function getOrCreateField(pageKey, formId, fieldName, type, fieldCategories) {
  const s = getState();
  const key = pageFormFieldKey(pageKey, formId, fieldName);
  let fld = s.fields.get(key);
  if (!fld) {
    fld = createFieldAgg(fieldName, type);
    s.fields.set(key, fld);
    const form = getOrCreateForm(pageKey, formId);
    form.fields.set(fieldName, fld);
  } else if (fld.type !== type) {
    fld.type = type;
  }
  mergeUnique(fld.field_categories, fieldCategories);
  return fld;
}

export function getOrCreateSection(pageKey, sectionId, sectionCategories) {
  const s = getState();
  const key = pageSectionKey(pageKey, sectionId);
  let sec = s.sections.get(key);
  if (!sec) {
    sec = createSectionAgg();
    sec.pageKey = pageKey;
    sec.sectionId = sectionId;
    s.sections.set(key, sec);
  }
  mergeUnique(sec.section_categories, sectionCategories);
  return sec;
}

/** Mark form focus-in by a field. */
export function markFormFocusIn(form, t, options) {
  const countVisit = !(options && options.countVisit === false);
  if (form.runtime.firstFocusAt === 0) form.runtime.firstFocusAt = t;
  if (!form.runtime.currentlyFocused) {
    form.runtime.currentlyFocused = true;
    form.runtime.activeSince = t;
    if (countVisit) {
      form.runtime.visitsSeen += 1;
      if (t - (form.runtime.lastVisitMarkAt || 0) >= VISIT_DEBOUNCE_MS) {
        form.runtime.lastVisitMarkAt = t;
        form.visit_count_lifetime = bumpFormLifetime(form.pageKey, form.formId, t);
      }
      form.visit_count_session += 1;
    }
  }
}

/** Mark form focus-out by a field; defers actual "out" if focus stays in form (handled by caller). */
export function markFormFocusOut(form, t) {
  if (form.runtime.currentlyFocused && form.runtime.activeSince > 0) {
    form.active_time_on_form_ms += Math.max(0, t - form.runtime.activeSince);
    form.runtime.activeSince = 0;
  }
  form.runtime.currentlyFocused = false;
  form.runtime.lastFocusOutAt = t;
  if (form.runtime.firstFocusAt > 0) {
    form.time_on_form_ms = Math.max(form.time_on_form_ms, t - form.runtime.firstFocusAt);
  }
}

/** Record the latest field value change used to measure the final-submit decision pause. */
export function markFieldChange(form, fieldName, t = Date.now()) {
  form.runtime.lastInputFieldName = fieldName;
  const session = getState().session;
  session.lastFieldChangeAt = t;
  session.pauseBeforeSubmitMs = null;
}

/** Returns true if `inner` is contained in `outer` (or equals it). */
export function elementContains(outer, inner) {
  if (!outer || !inner) return false;
  if (outer === inner) return true;
  return !!(outer.contains && outer.contains(inner));
}

function mergeUnique(target, values) {
  if (!Array.isArray(target) || !Array.isArray(values)) return;

  for (let i = 0; i < values.length; i++) {
    if (!target.includes(values[i])) target.push(values[i]);
  }
}
